/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import { AssetTypes } from '@src/hooks/studioContext/CMSParams';
import { elementTypes } from '../../../elements';
import { isTextBoxAttachmentDependent } from '../../../utils/textBoxAttachment';
import {
  AnimationPanel,
  BorderRadiusPanel,
  BorderStylePanel,
  CaptionsPanel,
  EffectsPanel,
  FilterPanel,
  ImageAccessibilityPanel,
  LinkPanel,
  LayerStylePanel,
  LottieParametersPanel,
  LottiePlaybackPanel,
  LutFilterPanel,
  PageAttachmentPanel,
  PageBackgroundPanel,
  ShapeStylePanel,
  SizePositionPanel,
  TextBorderStylePanel,
  TextBoxPanel,
  BackgroundBlurPanel,
  TextPinningPanel,
  TextStylePanel,
  VideoAccessibilityPanel,
  ElementAlignmentPanel,
  VideoOptionsPanel,
  StylePresetPanel,
  ColorPresetPanel,
  PageBackgroundAudioPanel,
} from '../../panels/design';
import AutomationKeyPanel from '../../panels/design/automationKey/automationKey';
import PanelTypes from '../../panels/design/types';

const ALL = Object.values(PanelTypes);

function intersect(a, b) {
  return a.filter((v) => b.includes(v));
}

function getDesignPanelsForSelection(elements, user, assetType) {
  if (elements.length === 0) {
    return [
      {
        type: PanelTypes.PAGE_BACKGROUND_AUDIO,
        Panel: PageBackgroundAudioPanel,
      },
    ];
  }

  const isBackground = elements.length === 1 && elements[0].isBackground;
  const areAnimationsSupported = [
    AssetTypes.Story,
    AssetTypes.Clip,
    AssetTypes.Ad,
    AssetTypes.Template,
    AssetTypes.CardsAsset,
  ].includes(assetType);
  // Only display background panel in case of background element.
  if (isBackground) {
    const panels = [];

    const isBackgroundMedia = !elements[0].isDefaultBackground;
    if (isBackgroundMedia) {
      panels.push({
        type: PanelTypes.PAGE_BACKGROUND,
        Panel: PageBackgroundPanel,
      });
      panels.push({
        type: PanelTypes.FILTER,
        Panel: FilterPanel,
      });
      if (elements[0].type === 'image') {
        panels.push({
          type: PanelTypes.LUT_FILTER,
          Panel: LutFilterPanel,
        });
      }
      if (areAnimationsSupported) {
        panels.push({ type: PanelTypes.ANIMATION, Panel: AnimationPanel });
      }
    }

    // If the selected element's type is video / image , display accessibility panel, too.
    if ('video' === elements[0].type) {
      // If the video doesn't have audio, don't show the mute/unmute button
      if (elements[0].resource && !elements[0].resource.noAudio) {
        panels.push({
          type: PanelTypes.VIDEO_OPTIONS,
          Panel: VideoOptionsPanel,
        });
      }
      // panels.push({
      //   type: PanelTypes.CAPTIONS,
      //   Panel: CaptionsPanel,
      // });
      // panels.push({
      //   type: PanelTypes.VIDEO_ACCESSIBILITY,
      //   Panel: VideoAccessibilityPanel,
      // });
    } else if (['gif', 'image'].includes(elements[0].type)) {
      panels.push({
        type: PanelTypes.IMAGE_ACCESSIBILITY,
        Panel: ImageAccessibilityPanel,
      });
      // In case of default background without media:
    } else {
      panels.unshift({
        type: PanelTypes.PAGE_BACKGROUND,
        Panel: PageBackgroundPanel,
      });
    }

    panels.push({
      type: PanelTypes.PAGE_ATTACHMENT,
      Panel: PageAttachmentPanel,
    });

    if (user.isSystemAdmin) {
      panels.push({
        type: PanelTypes.AUTOMATION_KEY,
        Panel: AutomationKeyPanel,
      });
    }

    return panels;
  }

  // Find which panels all the selected elements have in common
  const panels = elements
    .map(({ type }) => elementTypes[type].panels)
    .reduce((commonPanels, panels) => intersect(commonPanels, panels), ALL)
    .filter(
      (x) =>
        (x != PanelTypes.ANIMATION || areAnimationsSupported) &&
        x != PanelTypes.CAPTIONS
    );

  if (elements.length === 1 && isTextBoxAttachmentDependent(elements[0])) {
    const anchorPanel =
      elements[0].type === 'text'
        ? PanelTypes.TEXT_BOX
        : PanelTypes.SIZE_POSITION;
    const anchorIndex = panels.indexOf(anchorPanel);
    panels.splice(
      anchorIndex === -1 ? panels.length : anchorIndex + 1,
      0,
      PanelTypes.TEXT_PINNING
    );
  }

  if (elements.length === 1 && elements[0].type === 'image') {
    const filterIndex = panels.indexOf(PanelTypes.FILTER);
    panels.splice(
      filterIndex === -1 ? panels.length : filterIndex + 1,
      0,
      PanelTypes.LUT_FILTER
    );
  }

  if (user.isSystemAdmin) {
    panels.push(PanelTypes.AUTOMATION_KEY);
  }

  return panels
    .map((type) => {
      switch (type) {
        case PanelTypes.ANIMATION:
          return { type, Panel: AnimationPanel };
        case PanelTypes.PAGE_BACKGROUND:
          // Only display when isBackground.
          return null;
        case PanelTypes.PAGE_BACKGROUND_AUDIO:
          return { type, Panel: PageBackgroundAudioPanel };
        case PanelTypes.COLOR_PRESETS:
          return { type, Panel: ColorPresetPanel };
        case PanelTypes.EFFECTS:
          return { type, Panel: EffectsPanel };
        case PanelTypes.STYLE_PRESETS:
          return { type, Panel: StylePresetPanel };
        case PanelTypes.LAYER_STYLE:
          return { type, Panel: LayerStylePanel };
        case PanelTypes.FILTER:
          return { type, Panel: FilterPanel };
        case PanelTypes.SIZE_POSITION:
          return { type, Panel: SizePositionPanel };
        case PanelTypes.LINK:
          return { type, Panel: LinkPanel };
        case PanelTypes.LOTTIE_PARAMETERS:
          return { type, Panel: LottieParametersPanel };
        case PanelTypes.LOTTIE_PLAYBACK:
          return { type, Panel: LottiePlaybackPanel };
        case PanelTypes.LUT_FILTER:
          return { type, Panel: LutFilterPanel };
        case PanelTypes.TEXT_BORDER:
          return { type, Panel: TextBorderStylePanel };
        case PanelTypes.TEXT_STYLE:
          return { type, Panel: TextStylePanel };
        case PanelTypes.BACKGROUND_BLUR:
          return { type, Panel: BackgroundBlurPanel };
        case PanelTypes.TEXT_BOX:
          return { type, Panel: TextBoxPanel };
        case PanelTypes.TEXT_PINNING:
          return { type, Panel: TextPinningPanel };
        case PanelTypes.SHAPE_STYLE:
          return { type, Panel: ShapeStylePanel };
        case PanelTypes.BORDER_RADIUS:
          return { type, Panel: BorderRadiusPanel };
        case PanelTypes.BORDER:
          return { type, Panel: BorderStylePanel };

        case PanelTypes.VIDEO_OPTIONS:
          return { type, Panel: VideoOptionsPanel };
        case PanelTypes.CAPTIONS:
          return { type, Panel: CaptionsPanel };
        case PanelTypes.VIDEO_ACCESSIBILITY:
          return { type, Panel: VideoAccessibilityPanel };
        case PanelTypes.IMAGE_ACCESSIBILITY:
          return { type, Panel: ImageAccessibilityPanel };
        case PanelTypes.ELEMENT_ALIGNMENT:
          return { type, Panel: ElementAlignmentPanel };
        case PanelTypes.AUTOMATION_KEY:
          return { type, Panel: AutomationKeyPanel };
        default:
          throw new Error(`Unknown panel: ${type}`);
      }
    })
    .filter((panel) => panel);
}

export default getDesignPanelsForSelection;
